import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, useLocation, useMatches, useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import Card from '../../components/Card';
import Icon, { Icons } from '../../components/Icon/Icon';
import ListItem from '../../components/ListItem';
import PageHeader from '../../components/PageHeader';
import AppConfig from '../../config';
import { appSlice } from '../../store/slices';

import './CardDetails.scss';

const { routes } = AppConfig;

const CardDetails = () => {
	const locationState = useLocation().state || {};
	const { cardInfo = {} } = locationState;

	const matches = useMatches();

	const navigate = useNavigate();
	const dispatch = useDispatch();

	const deviceInfo = useSelector(state => state.app.deviceInfo);
	const osName = deviceInfo?.osName;

	const currentHeader = [...matches].reverse().find(m => m.handle?.header)?.handle.header || '';

	const [flipStatus, setFlipStatus] = useState(false);
	const [flipSignal, setFlipSignal] = useState('');

	const isFrozenCard = cardInfo.Status === 'frozen';
	const isAndroid = osName === 'Android';

	const goToWalletInstruction = operationSystem => {
		navigate({
			pathname: routes.addCardToWallet,
			search: createSearchParams({ operationSystem: operationSystem }).toString(),
		});
	};

	const menuNavigate = route => {
		if (route === 'unfreeze' || route === 'freeze') {
			freezeUnfreezeCard();
		} else if (route === 'close') {
			closeCard();
		} else if (routes[route]) {
			navigate(routes[route]);
		}
	};

	const closeCard = () => {
		dispatch(
			appSlice.actions.addModal({
				tag: 'CloseCard',
				outsideParams: { cardId: cardInfo.Id },
			})
		);
	};

	const freezeUnfreezeCard = () => {
		dispatch(
			appSlice.actions.addModal({
				isHeaderWithColorBgAndIcon: true,
				title: !isFrozenCard ? 'Freeze card' : 'Unfreeze card',
				subtitle: 'Learn about freeze card',
				headerBackgroundColor: '#0455A3',
				headerIcon: 'SNOWFLAKE_SLASH_BOLD',
				tag: 'FreezeCard',
				headClass: 'FreezeCard',
				isOpen: true,
				height: '70%',
				outsideParams: { mode: !isFrozenCard },
			})
		);
	};

	const Menuitems1 = [
		{
			id: 'personalInfo',
			icon: Icons.PERSON_CIRCLE,
			title: 'Personal info',
		},
		{
			id: 'cardTerms',
			icon: Icons.CREDIT_CARD_FILL,
			title: 'Fees, Terms, Conditions',
		},
		{
			id: 'cardEdit',
			icon: Icons.PAINT_BRUSH,
			title: 'Design card',
		},
	];

	const Menuitems2 = [
		{
			id: isFrozenCard ? 'unfreeze' : 'freeze',
			icon: isFrozenCard ? Icons.SNOWFLAKE : Icons.SNOWFLAKE_SLASH,
			title: isFrozenCard ? 'Unfreeze card' : 'Freeze card',
			additionalClass: 'withoutArrow',
		},
		{
			id: 'close',
			icon: Icons.XMARK_CIRCLE,
			title: 'Close card',
			additionalClass: 'withoutArrow redIconColor',
		},
	];

	const renderItem = (item, index, array) => {
		return (
			<ListItem
				className={item.additionalClass ? ' ' + item.additionalClass : ''}
				key={item.id}
				title={item.title}
				description={item.description && <span>{item.description}</span>}
				icon={
					item.icon && (
						<div className='CardDetailsNavIcon'>
							<Icon type={item.icon} width={20} height={20} />
						</div>
					)
				}
				bottomSeparator={index !== array.length - 1}
				rightTitle={<Icon type={Icons.ARROW_CHEVRON} />}
				onSelect={() => menuNavigate(item.id)}
			/>
		);
	};

	return (
		<div className='CardDetails'>
			<PageHeader header={currentHeader} />
			<div className='CardDetailsBody'>
				<div className='CardDetailsCard'>
					<Card
						color={cardInfo.Color}
						Id={cardInfo.Id}
						number={cardInfo.Number}
						cardholderName={cardInfo.CardholderName}
						paymentSystem={cardInfo.PaymentSystem}
						status={cardInfo.Status}
						copyNumberByLongPress
						flipCardByPress
						setFlipStatus={setFlipStatus}
						flipSignal={flipSignal}
					/>
				</div>

				{!isFrozenCard && (
					<Button
						iconBefore={!flipStatus ? 'EYE_SLASH' : 'EYE_ICON'}
						styleType='secondary'
						onClick={() => setFlipSignal(!flipStatus ? 'showDetails' : 'hideDetails')}
					>
						{!flipStatus ? 'View card details' : 'Hide card details'}
					</Button>
				)}
				{isFrozenCard && (
					<Button iconBefore='SNOWFLAKE' styleType='secondary' onClick={() => menuNavigate('unfreeze')}>
						{'Unfreeze card'}
					</Button>
				)}

				<div
					className={'AddToWallet' + (isFrozenCard ? ' isFrozenCard' : '')}
					onClick={() => {
						if (!isFrozenCard) {
							goToWalletInstruction(isAndroid ? 'Android' : 'iOS');
						}
					}}
				>
					<Icon
						type={Icons[isAndroid ? 'GOOGLE_WALLET' : 'APPLE_LOGO']}
						width={32}
						height={32}
						className={isAndroid ? '' : 'AppleLogo'}
					/>
					{isAndroid ? 'Add to Google Wallet' : 'Add to Apple Wallet'}
				</div>

				<div className='CardDetailsNav'>{Menuitems1.map(renderItem)}</div>

				<div className='CardDetailsNav'>{Menuitems2.map(renderItem)}</div>
			</div>
		</div>
	);
};

export default CardDetails;
